import React, {FC} from 'react';

import {FrontWindow} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isHtmlElement} from '../../../libs/shared/core/src/helpers/browser/domHelpers';
import {MouseEventHandler} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CalendarsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {useLayer} from '../../../libs/shared/ui/src/components/layers/layerContext';
import {
  buildFullCalendarArbitraryEventId,
  fullCalendarArbitraryEventIdPrefix,
} from '../../../srcCommon/helpers/calendarHelpers';
import {
  ArbitraryCalendarEventData,
  CalendarEventData,
} from '../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventGridParameters,
  FocusedEventSourcesEnum,
} from '../../store/panels/calendars/calendarsPanelState';
import {LargeCalendarsEventEditorContainer} from './editor/largeCalendarsEventEditorContainer';
import {CalendarsGridContainer} from './grid/calendarsGridContainer';
import {buildFullCalendarEventId, fullCalendarEventIdPrefix} from './grid/fullCalendarHelpers';
import {LargeCalendarsBase, RenderLargeCalendarsLeftPanel} from './largeCalendarsBase';
import {PopoverArbitraryCalendarEventViewer} from './viewer/popoverArbitraryCalendarEventViewer';
import {PopoverCalendarEventViewerContainer} from './viewer/popoverCalendarEventViewerContainer';

/*
 * Props.
 */

interface LargeCalendarsProps {
  panelId: CalendarsPanelId;
  renderLeftPanel: RenderLargeCalendarsLeftPanel;
  hasNoCalendars: boolean;
  shouldCalendarDisplayWeekends: boolean;
  focusedCalendarEventData: CalendarEventData | undefined;
  popoverCalendarEventData: CalendarEventData | undefined;
  popoverArbitraryCalendarEventData: ArbitraryCalendarEventData | undefined;
  onEventClick: (parameters: CalendarEventGridParameters) => void;
  onPopoverRequestClose: () => void;
  focusedDate: number;
  onFocusedDateSelect: (arg: number) => void;
  leftPanelWidth: number;
  onLeftPanelWidthChange: (arg: number) => void;
}

/*
 * Component.
 */

export const LargeCalendars: FC<LargeCalendarsProps> = (props) => {
  const {window} = useLayer();

  const onGridMouseDown: MouseEventHandler = (event) => {
    if (!props.popoverCalendarEventData && !props.popoverArbitraryCalendarEventData) {
      return;
    }

    // Dismiss the popover on click unless we're clicking another event on the grid.
    const eventTarget = isHtmlElement(event.target) && event.target;
    const hasFullCalendarIdPrefix =
      eventTarget &&
      eventTarget.id &&
      (eventTarget.id.startsWith(fullCalendarEventIdPrefix) ||
        eventTarget.id.startsWith(fullCalendarArbitraryEventIdPrefix));
    if (hasFullCalendarIdPrefix) {
      return;
    }

    props.onPopoverRequestClose();
  };

  return (
    <LargeCalendarsBase
      panelId={props.panelId}
      renderLeftPanel={props.renderLeftPanel}
      renderRightPanel={() => maybeRenderCalendarEventEditor(props)}
      renderPopover={() => maybeRenderCalendarEventPopover(props, window)}
      renderGrid={() => renderGrid(props)}
      hasNoCalendars={props.hasNoCalendars}
      onGridMouseDown={onGridMouseDown}
      focusedDate={props.focusedDate}
      onFocusedDateSelect={props.onFocusedDateSelect}
      leftPanelWidth={props.leftPanelWidth}
      onLeftPanelWidthChange={props.onLeftPanelWidthChange}
      shouldCombineCalendarsList={false}
      shouldSaveCalendarVersion
    />
  );
};

function maybeRenderCalendarEventEditor(props: LargeCalendarsProps) {
  const {panelId, focusedCalendarEventData} = props;
  if (!focusedCalendarEventData) {
    return undefined;
  }

  return (
    <LargeCalendarsEventEditorContainer panelId={panelId} calendarEventData={focusedCalendarEventData} />
  );
}

function maybeRenderCalendarEventPopover(props: LargeCalendarsProps, window: FrontWindow) {
  // First check if the popover is for an arbitrary event.
  if (props.popoverArbitraryCalendarEventData) {
    const {arbitraryCalendarEvent, color} = props.popoverArbitraryCalendarEventData;
    const poppoverArbitraryCalendarEventId = buildFullCalendarArbitraryEventId(arbitraryCalendarEvent.id);

    const eventGridItem = window.document.getElementById(poppoverArbitraryCalendarEventId);
    const eventGridItemRect = eventGridItem?.getBoundingClientRect();

    if (eventGridItemRect) {
      return (
        <PopoverArbitraryCalendarEventViewer
          anchor={() => eventGridItemRect}
          arbitraryCalendarEvent={arbitraryCalendarEvent}
          color={color}
          onRequestClose={props.onPopoverRequestClose}
        />
      );
    }
  }

  // Next check if there is a popover for a non-arbitrary event.
  if (!props.popoverCalendarEventData) {
    return null;
  }

  const {calendarEventVersion, calendarVersion, instanceStartDate, source} = props.popoverCalendarEventData;
  const fullCalendarEventId = buildFullCalendarEventId(
    calendarEventVersion.uid,
    calendarVersion.id,
    instanceStartDate,
    source === FocusedEventSourcesEnum.MORE_EVENTS_POPOVER,
  );

  const eventGridItem = window.document.getElementById(fullCalendarEventId);
  const eventGridItemRect = eventGridItem?.getBoundingClientRect();
  if (!eventGridItemRect) {
    return null;
  }

  return (
    <PopoverCalendarEventViewerContainer
      panelId={props.panelId}
      anchor={() => eventGridItemRect}
      calendarEventData={props.popoverCalendarEventData}
      onRequestClose={props.onPopoverRequestClose}
    />
  );
}

function renderGrid(props: LargeCalendarsProps) {
  return (
    <CalendarsGridContainer
      panelId={props.panelId}
      popoverCalendarEventData={props.popoverCalendarEventData}
      onEventClick={props.onEventClick}
      canCreateEventOnSelect={!props.popoverCalendarEventData && !props.popoverArbitraryCalendarEventData}
      shouldCalendarDisplayWeekends={props.shouldCalendarDisplayWeekends}
    />
  );
}
